import React from "react";
import { createPortal } from "react-dom";
import { Box, Button, Flex, IconButton, Text } from "@chakra-ui/react";
import { useLocation } from "react-router-dom";
import { IconClose } from "../../icons";
import { MARKETING_LOCALES } from "../../marketing-content/marketingLocales";
import {
  getMarketingLanguageSuggestion, hasMarketingLanguageChoice, MARKETING_LANGUAGE_CHOICE_EVENT, rememberMarketingLanguageChoice,
} from "../../marketing-content/marketingLanguageSuggestion";
import { getMarketingPageLanguage } from "../../marketing-content/marketingPageTranslations";
import { getMarketingTranslations, loadMarketingTranslations, MarketingTranslations } from "../../marketing-content/marketingTranslations";
import { useLandingTheme } from "../landing-page/LandingThemeContext";
import "../../fonts/localization-fonts/languageNames.css";

type Suggestion = NonNullable<ReturnType<typeof getMarketingLanguageSuggestion>> & {
  pathname: string;
  copy: MarketingTranslations["site"]["languageSuggestion"];
};

/** Enhances the unchanged, server-rendered page after hydration; never redirects or takes focus. */
export function MarketingLanguageSuggestion() {
  const { pathname, search, hash } = useLocation();
  const theme = useLandingTheme();
  const labelId = React.useId();
  const [suggestion, setSuggestion] = React.useState<Suggestion | null>(null);

  React.useEffect(() => {
    let current = true;
    setSuggestion(null);
    // Visiting an explicit translated URL is a language choice, including a later fallback to English.
    const pageLanguage = getMarketingPageLanguage(pathname);
    if (pageLanguage && pageLanguage !== "en") rememberMarketingLanguageChoice();
    const clear = () => setSuggestion(null);
    window.addEventListener(MARKETING_LANGUAGE_CHOICE_EVENT, clear);
    if (!hasMarketingLanguageChoice()) {
      const candidate = getMarketingLanguageSuggestion(pathname, navigator.languages?.length ? navigator.languages : [navigator.language]);
      if (candidate) {
        void loadMarketingTranslations(candidate.language).then(() => {
          if (current && !hasMarketingLanguageChoice()) {
            setSuggestion({ ...candidate, pathname, copy: getMarketingTranslations(candidate.language).site.languageSuggestion });
          }
        }).catch(() => { /* An optional suggestion must never interrupt the page or its language selector. */ });
      }
    }
    return () => {
      current = false;
      window.removeEventListener(MARKETING_LANGUAGE_CHOICE_EVENT, clear);
    };
  }, [pathname]);

  if (!suggestion || suggestion.pathname !== pathname) return null;
  const locale = MARKETING_LOCALES[suggestion.language];
  // Keep the optional controls after the page in keyboard and landmark order,
  // even when a navigation header owns this component.
  return createPortal(
    <Box
      as="aside" aria-labelledby={labelId} lang={locale.htmlLang} dir={locale.direction}
      data-marketing-language-suggestion={suggestion.language}
      position="fixed" zIndex={40} insetInlineEnd={{ base: "12px", md: "24px" }}
      bottom="max(16px, env(safe-area-inset-bottom))" w={{ base: "calc(100% - 24px)", md: "360px" }} maxW="360px"
      bg={theme.color.surface} color={theme.color.heading} border={`1px solid ${theme.color.border}`} borderRadius="20px"
      boxShadow="0 12px 40px rgba(0,0,0,.18)" p="16px"
      fontFamily={'system-ui, "Jungle Language Names Devanagari", sans-serif'}
      onKeyDown={event => { if (event.key === "Escape") rememberMarketingLanguageChoice(); }}
    >
      <Flex align="flex-start" gap="8px">
        <Box flex="1" minW={0}>
          <Text id={labelId} fontSize="17px" fontWeight={700} lineHeight="1.5"><bdi>{locale.name}</bdi></Text>
          <Text mt="4px" fontSize="14px" lineHeight="1.6" color={theme.color.text}>{suggestion.copy.message}</Text>
        </Box>
        <IconButton aria-label={suggestion.copy.dismiss} icon={<IconClose width={16} height={16} color="currentColor" />}
          variant="ghost" color={theme.color.heading} minW="44px" h="44px" mt="-8px" me="-8px" flexShrink={0}
          onClick={rememberMarketingLanguageChoice} _hover={{ bg: theme.color.accentSoft }}
          _focusVisible={{ outline: "2px solid currentColor", outlineOffset: "2px" }} />
      </Flex>
      <Button as="a" href={`${suggestion.href}${search}${hash}`} hrefLang={locale.hreflang}
        onClick={rememberMarketingLanguageChoice} mt="12px" w="100%" minH="44px" h="auto" py="10px" px="16px"
        whiteSpace="normal" textAlign="center" fontSize="15px" lineHeight="1.5" borderRadius={theme.shape.buttonRadius}
        bg={theme.color.accent} color={theme.button.primaryColor}
        _hover={{ bg: theme.color.accentHover, textDecoration: "none" }}
        _focusVisible={{ outline: `2px solid ${theme.color.heading}`, outlineOffset: "3px" }}>
        {suggestion.copy.switchLanguage}
      </Button>
    </Box>, document.body
  );
}
